import type { Message } from '../../flora.type';

/**
 * Result of splitting a flat, persisted chat history into per-turn question
 * and answer arrays, each `Message` stamped with a shared `turnId`.
 */
type DerivedTurns = {
  questions: Message[];
  answers: Message[];
};

/**
 * Deterministically pairs a flat, role-tagged backend history into per-turn
 * question/answer arrays by assigning a stable `turnId` to each `user`
 * message and to the `assistant` message(s) that follow it, up to (not
 * including) the next `user` message.
 *
 * Backend history has no per-message id, so pairing was previously done by
 * matching array position between two independently role-filtered arrays —
 * if a turn errored before an assistant reply was persisted, that
 * positional pairing silently shifted every later turn's answer onto the
 * wrong question. This helper instead assigns an id derived purely from
 * message order, so a `user` message with no following `assistant` reply
 * simply has no paired answer, instead of desynchronizing later turns.
 *
 * Idempotent and order-only: given the same persisted `messages` array
 * (whose order never changes once written), it always produces the same
 * `turnId`s, so a history that was already correctly paired renders
 * identically before and after this pairing scheme.
 *
 * `system` messages (e.g. `context_compacted` events) are passed through
 * untouched — callers keep their own logic for merging those into the
 * preceding answer.
 *
 * Assumes at most one persisted `assistant` message per user turn, matching
 * how a live turn's assistant message is always a single `Message` object
 * mutated via its `segments` as it streams, never multiple discrete
 * messages. If the backend ever persists more than one for one turn, every
 * message stays in `answers` (nothing is dropped here), but the turnId-keyed
 * `Map` lookups in the renderers only keep the last one for a given
 * `turnId` — see `agent-message-list.tsx`.
 *
 * An `assistant` message with no preceding `user` message (not expected in
 * normal operation) is kept in `answers` with `turnId: undefined` — it
 * cannot be looked up by any question's turnId, so it renders only for a
 * caller that still falls back to positional lookup.
 *
 * @param {Message[]} historyMessages - Raw `ChatHistory.messages`, in persisted order
 * @returns {DerivedTurns} `questions` and `answers`, each `Message` stamped with its turn's `turnId`
 */
function deriveTurnsFromHistory(historyMessages: Message[]): DerivedTurns {
  const questions: Message[] = [];
  const answers: Message[] = [];
  let turnIndex = 0;
  let currentTurnId: string | undefined;

  for (const message of historyMessages) {
    if (message.role === 'user') {
      currentTurnId = `hist-turn-${turnIndex}`;
      turnIndex += 1;
      questions.push({ ...message, turnId: currentTurnId });
    } else if (message.role === 'assistant') {
      answers.push({ ...message, turnId: currentTurnId });
    }
  }

  return { questions, answers };
}

/**
 * Monotonic sequence backing {@link createTurnId}. `Date.now()` alone
 * collides when two turns are submitted within the same millisecond (e.g. a
 * double-submit), which would make them indistinguishable for pairing. The
 * counter guarantees a unique id per turn.
 */
let turnSequence = 0;

/**
 * Generates a stable id for a live (not-yet-persisted) turn, used to stamp
 * a submitted question and match it against the assistant messages that
 * arrive for it over the websocket. Namespaced separately from the
 * `hist-turn-<n>` ids `deriveTurnsFromHistory` assigns to rehydrated
 * history, so a live id can never collide with a historical one.
 *
 * @returns {string} A unique, monotonically-ordered turn id
 */
function createTurnId(): string {
  turnSequence += 1;
  return `turn-${Date.now()}-${turnSequence}`;
}

export { createTurnId, deriveTurnsFromHistory };
